/** The hero card at the top of the home screen: the inventory total, the two scopes, where the
 * filing checks stand, and four small facts (the due date, filings owed, traceable, metered).
 *
 * **One dark card, and the order is the order of the questions a controller asks**: how much (the
 * total, with its change against the prior period), where it comes from (the scopes), and can I file
 * it (the verdict and the four facts). It is made of the same pieces the five light cards were - a
 * `KpiTile` per figure - so every figure keeps its receipt link, its delta and its drill link, and
 * every `data-kpi`, `kpi-delta` and `kpi-drill` hook the tests and the browser gate read survives.
 *
 * Three things it enforces, each of which an inline `<div>` would let a caller undo:
 *
 * - **Direction is an arrow and a word, never a colour.** The delta is drawn in the card's ink for
 *   every direction (F-040: colour is not the only channel, and a fall is not good news). A screen
 *   reader hears "Down 8.6% versus FY2024".
 * - **A delta is present or absent, never zero.** `delta` is optional and an omitted one draws
 *   nothing at all: no dash, no "0%". A change that was measured and is nil is `flat`, which says so.
 * - **No chart is drawn here.** The monthly series is below, where it says what it holds. A decorative
 *   bar strip on the total would be a picture the data did not draw.
 *
 * `KpiTile` owns the `figure` class names `ui-check` finds a clickable KPI by.
 */

import { ArrowDown, ArrowUp, Equals } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { KpiTile } from "../components/KpiTile";
import type { Direction } from "./readinessModel";
import styles from "./ReadinessKpis.module.css";

export interface KpiDelta {
  direction: Direction;
  /** The size of the move, no sign: `8.6%`, `12 pts`. */
  magnitude: string;
  /** What it is against: `FY2024`. */
  versus: string;
}

export interface ReadinessKpi {
  key: string;
  label: string;
  value: string;
  /** A value that is words and not a number (`Not checked`), set smaller so it fits the card. */
  textual?: boolean;
  unit?: string;
  delta?: KpiDelta;
  note?: string;
  noteStrong?: string;
  /** Opens the breakdown of the figures this one summed. Omitted when there is none to show. */
  onOpen?: () => void;
  openLabel?: string;
  /** The link to the rows behind the figure, which the screen builds so it carries the screen's own
   * window and filters (gate 28). */
  drill?: ReactNode;
  /** The thumbnail, already built from the screen's series. */
  trend?: ReactNode;
}

const WORDS: Record<Direction, string> = { up: "Up", down: "Down", flat: "No change" };

export function Delta({ delta }: { delta: KpiDelta }) {
  const Icon = delta.direction === "up" ? ArrowUp : delta.direction === "down" ? ArrowDown : Equals;
  return (
    <div className={styles.delta} data-testid="kpi-delta" data-direction={delta.direction}>
      <Icon size={12} weight="bold" aria-hidden="true" />
      <span className="visuallyHidden">
        {delta.direction === "flat" ? WORDS.flat : `${WORDS[delta.direction]} ${delta.magnitude}`} versus{" "}
        {delta.versus}
      </span>
      <span aria-hidden="true" className={styles.deltaText}>
        {delta.direction === "flat" ? (
          <>No change</>
        ) : (
          <b className="num">{delta.magnitude}</b>
        )}{" "}
        <span className={styles.versus}>vs {delta.versus}</span>
      </span>
    </div>
  );
}

/** The small facts, in the order they are read. A key this list does not know still draws, after. */
const FACT_ORDER = ["next-filing", "filings-owed", "traceable", "metered"];

function tile(item: ReadinessKpi, large = false) {
  return (
    <KpiTile
      key={item.key}
      kpi={item.key}
      tone="dark"
      size={large ? "large" : "normal"}
      label={item.label}
      value={item.value}
      textual={item.textual}
      unit={item.unit}
      delta={item.delta ? <Delta delta={item.delta} /> : undefined}
      note={item.note}
      noteStrong={item.noteStrong}
      onOpen={item.onOpen}
      openLabel={item.openLabel}
      footer={item.drill}
      trend={item.trend}
    />
  );
}

export function ReadinessKpis({
  items,
  eyebrow,
  scopes,
  verdict,
}: {
  items: readonly ReadinessKpi[];
  /** What the total is, in words: `Total inventory · FY2025`. */
  eyebrow: string;
  /** The scope split, already built by the screen (it owns the breakdown drawers). */
  scopes: ReactNode;
  /** The verdict card. */
  verdict: ReactNode;
}) {
  const total = items.find((item) => item.key === "inventory");
  const facts = items
    .filter((item) => item.key !== "inventory")
    .sort((a, b) => {
      const rank = (key: string) => {
        const at = FACT_ORDER.indexOf(key);
        return at === -1 ? FACT_ORDER.length : at;
      };
      return rank(a.key) - rank(b.key);
    });
  return (
    <div className={styles.hero} role="group" aria-label="Headline figures" data-testid="kpi-row">
      <div className={styles.left}>
        <div className={styles.eyebrow}>{eyebrow}</div>
        <div className={styles.total}>{total ? tile(total, true) : null}</div>
        <div className={styles.scopes}>{scopes}</div>
      </div>
      <div className={styles.right}>
        {verdict}
        <div className={styles.facts}>{facts.map((item) => tile(item))}</div>
      </div>
    </div>
  );
}
